/**
 * Raw-audio echo detector: is the far-end (loopback) waveform present in the mic?
 *
 * Envelope correlation cannot answer this — all human speech shares the same
 * ~4-5 Hz syllabic rhythm, so two DIFFERENT people talking have correlated
 * loudness envelopes whether or not one is an echo of the other. The only signal
 * that separates "same source, echoed" from "two independent voices" is on the
 * actual waveform: **magnitude-squared coherence** (and a **GCC-PHAT** delay
 * peak), the same tools acoustic echo cancellers use.
 *
 * Coherence C(f) = |E[X·Y*]|² / (E[|X|²]·E[|Y|²]) measures how linearly the mic
 * (X) is related to the loopback (Y) at each frequency, averaged over frames
 * (Welch). A true echo is a delayed, scaled copy ⇒ a CONSISTENT cross-spectrum
 * phase per bin ⇒ C→1. Two independent sources have random relative phase across
 * frames ⇒ the cross-spectrum averages to ~0 ⇒ C→0 — EVEN when their spectra
 * overlap. C is inherently normalized (0..1), so the decision threshold is a
 * physical constant, identical on every device — no per-device calibration.
 *
 * GCC-PHAT adds a whitened cross-correlation whose peak sits at the echo delay;
 * its prominence corroborates a genuine delayed copy and measures the delay for
 * free (device/pipeline/Bluetooth timing falls out automatically).
 *
 * Pure + DOM-free: fed windows of the two channels (already sample-aligned by a
 * shared AudioContext) and returns a verdict. Observe-only — drives nothing here.
 */

export type TFarEndInMicMode = "far-end-in-mic" | "far-end-absent" | "unknown"

export interface IEchoCoherenceDiagnostics {
  /** Power-weighted mean coherence in the speech band (0..1) — the decision signal. */
  meanCoherence: number
  /** Estimated echo delay in samples (GCC-PHAT peak); >0 ⇒ mic lags the loopback. */
  delaySamples: number
  /** GCC-PHAT peak height relative to its own baseline — delayed-copy corroboration. */
  peakProminence: number
  /** Frames that passed the far-end-active gate and fed the estimate. */
  activeFrames: number
}

export interface IEchoCoherenceResult {
  mode: TFarEndInMicMode
  /** Evidence strength for {@link mode}, 0..1. */
  confidence: number
  diagnostics?: IEchoCoherenceDiagnostics
}

export interface IEchoCoherenceOptions {
  /** FFT frame size (power of two). Default 1024. */
  frameSize?: number
  /** Hop between frames in samples (Welch overlap). Default frameSize/2. */
  hop?: number
  /** Speech band (Hz) the coherence is aggregated over. Default [150, 4000]. */
  bandHz?: [number, number]
  /** Frame is "far-end active" when its loopback energy ≥ this fraction of the
   *  window's peak frame energy. Relative ⇒ gain-invariant. Default 0.35. */
  activityFraction?: number
  /** Absolute loopback-energy floor (peak, per sample) below which the channel is
   *  treated as silent ⇒ unknown. Presence, not a speech threshold. Default 1e-5. */
  presenceFloor?: number
  /** Minimum far-end-active frames before a confident verdict. Default 8. */
  minActiveFrames?: number
  /** Coherence at/above which the far-end is judged present in the mic. A physical
   *  constant (coherence is normalized), the same on every device. Default 0.4. */
  coherenceThreshold?: number
  /** Max |bulk delay| (samples) searched to align the two capture pipelines before
   *  framing — the mic and loopback reach their analysers via different paths, so a
   *  tens-of-ms offset is normal. Default ~100ms (`0.1·sampleRate`). */
  maxDelaySamples?: number
}

const DEFAULT_FRAME_SIZE = 1024
const DEFAULT_BAND_HZ: [number, number] = [150, 4000]
const DEFAULT_ACTIVITY_FRACTION = 0.35
const DEFAULT_PRESENCE_FLOOR = 1e-5
const DEFAULT_MIN_ACTIVE_FRAMES = 8
const DEFAULT_COHERENCE_THRESHOLD = 0.4
const EPS = 1e-12

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/**
 * In-place iterative radix-2 Cooley-Tukey FFT. `re`/`im` length must be a power
 * of two. Forward transform (negative exponent).
 */
export function fftInPlace(re: Float64Array, im: Float64Array): void {
  const n = re.length
  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1
    for (; j & bit; bit >>= 1) j ^= bit
    j ^= bit
    if (i < j) {
      const tr = re[i]
      re[i] = re[j]
      re[j] = tr
      const ti = im[i]
      im[i] = im[j]
      im[j] = ti
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len
    const wr = Math.cos(ang)
    const wi = Math.sin(ang)
    for (let i = 0; i < n; i += len) {
      let cr = 1
      let ci = 0
      const half = len >> 1
      for (let k = 0; k < half; k++) {
        const a = i + k
        const b = a + half
        const xr = re[b] * cr - im[b] * ci
        const xi = re[b] * ci + im[b] * cr
        re[b] = re[a] - xr
        im[b] = im[a] - xi
        re[a] += xr
        im[a] += xi
        const ncr = cr * wr - ci * wi
        ci = cr * wi + ci * wr
        cr = ncr
      }
    }
  }
}

/** Inverse FFT via conjugation. `re`/`im` overwritten with the time-domain result. */
function ifftInPlace(re: Float64Array, im: Float64Array): void {
  const n = re.length
  for (let i = 0; i < n; i++) im[i] = -im[i]
  fftInPlace(re, im)
  for (let i = 0; i < n; i++) {
    re[i] /= n
    im[i] = -im[i] / n
  }
}

/** Periodic Hann window of length `n`. */
function hann(n: number): Float64Array {
  const w = new Float64Array(n)
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / n)
  return w
}

/** Largest power of two ≤ n. */
function largestPow2(n: number): number {
  let p = 1
  while (p * 2 <= n) p *= 2
  return p
}

/**
 * Estimate the bulk delay (samples) of `mic` relative to `loop` via a full-window
 * GCC-PHAT cross-correlation — whitened so the peak is sharp regardless of
 * spectral colour. Positive ⇒ mic LAGS the loopback (the normal echo direction).
 * Searched over ±maxDelay; returns 0 with low prominence when nothing aligns
 * (independent signals), which is harmless because coherence is delay-invariant.
 */
function estimateBulkDelay(
  mic: Float32Array | readonly number[],
  loop: Float32Array | readonly number[],
  size: number,
  maxDelay: number,
): { delay: number; prominence: number } {
  const w = hann(size)
  const xr = new Float64Array(size)
  const xi = new Float64Array(size)
  const yr = new Float64Array(size)
  const yi = new Float64Array(size)
  for (let i = 0; i < size; i++) {
    xr[i] = mic[i] * w[i]
    yr[i] = loop[i] * w[i]
  }
  fftInPlace(xr, xi)
  fftInPlace(yr, yi)
  // PHAT-weighted cross-spectrum: (X·conj(Y)) / |X·conj(Y)|.
  for (let b = 0; b < size; b++) {
    const cr = xr[b] * yr[b] + xi[b] * yi[b]
    const ci = xi[b] * yr[b] - xr[b] * yi[b]
    const mag = Math.hypot(cr, ci)
    if (mag < EPS) {
      xr[b] = 0
      xi[b] = 0
    } else {
      xr[b] = cr / mag
      xi[b] = ci / mag
    }
  }
  ifftInPlace(xr, xi)
  const cap = Math.min(maxDelay, size >> 1)
  let peak = -1
  let peakLag = 0
  let sumAbs = 0
  let count = 0
  for (let lag = -cap; lag <= cap; lag++) {
    const idx = (lag + size) % size
    const v = Math.abs(xr[idx])
    sumAbs += v
    count++
    if (v > peak) {
      peak = v
      peakLag = lag
    }
  }
  const baseline = count > 0 ? sumAbs / count : 0
  return { delay: peakLag, prominence: baseline > EPS ? peak / baseline : 0 }
}

/**
 * Analyze whether `loop` (far-end) is present in `mic` over the supplied window
 * of aligned PCM. Both buffers are the same length; extra samples past the last
 * whole hop are ignored. See the file header for the coherence rationale.
 */
export function analyzeEchoCoherence(
  mic: Float32Array | readonly number[],
  loop: Float32Array | readonly number[],
  sampleRate: number,
  opts?: IEchoCoherenceOptions,
): IEchoCoherenceResult {
  const frameSize = opts?.frameSize ?? DEFAULT_FRAME_SIZE
  const hop = opts?.hop ?? frameSize >> 1
  const [loBand, hiBand] = opts?.bandHz ?? DEFAULT_BAND_HZ
  const activityFraction = opts?.activityFraction ?? DEFAULT_ACTIVITY_FRACTION
  const presenceFloor = opts?.presenceFloor ?? DEFAULT_PRESENCE_FLOOR
  const minActiveFrames = opts?.minActiveFrames ?? DEFAULT_MIN_ACTIVE_FRAMES
  const coherenceThreshold = opts?.coherenceThreshold ?? DEFAULT_COHERENCE_THRESHOLD
  const maxDelaySamples = opts?.maxDelaySamples ?? Math.round(0.1 * sampleRate)

  const n = Math.min(mic.length, loop.length)
  if (n < frameSize) return { mode: "unknown", confidence: 0 }

  const win = hann(frameSize)
  const bins = frameSize >> 1
  const loBin = Math.max(1, Math.floor((loBand * frameSize) / sampleRate))
  const hiBin = Math.min(bins - 1, Math.ceil((hiBand * frameSize) / sampleRate))

  // Align the two capture pipelines first: the mic and loopback reach their
  // analysers by different paths, so a tens-of-ms bulk offset is normal and would
  // otherwise wreck the per-frame coherence. A full-window GCC-PHAT measures it;
  // then the mic frame is read at loopStart + bulkDelay. (Coherence is itself
  // delay-invariant, so aligning independent signals never manufactures coherence.)
  let bulkDelay = 0
  let peakProminence = 0
  const alignSize = largestPow2(n)
  if (alignSize >= 2 * frameSize) {
    const est = estimateBulkDelay(mic, loop, alignSize, maxDelaySamples)
    bulkDelay = est.delay
    peakProminence = est.prominence
  }

  // First pass: valid aligned frames + per-frame loopback energy → relative
  // activity gate (gain-invariant).
  const frameStarts: number[] = []
  const frameLoopEnergy: number[] = []
  let peakEnergy = 0
  for (let start = 0; start + frameSize <= n; start += hop) {
    const micStart = start + bulkDelay
    if (micStart < 0 || micStart + frameSize > n) continue
    let e = 0
    for (let k = 0; k < frameSize; k++) {
      const s = loop[start + k]
      e += s * s
    }
    frameStarts.push(start)
    frameLoopEnergy.push(e)
    if (e > peakEnergy) peakEnergy = e
  }
  if (peakEnergy < presenceFloor * frameSize) return { mode: "unknown", confidence: 0 }
  const activeEnergyFloor = activityFraction * peakEnergy

  // Accumulate cross- and auto-spectra over far-end-active, aligned frames (Welch).
  const Sxx = new Float64Array(bins)
  const Syy = new Float64Array(bins)
  const SxyRe = new Float64Array(bins)
  const SxyIm = new Float64Array(bins)
  const xr = new Float64Array(frameSize)
  const xi = new Float64Array(frameSize)
  const yr = new Float64Array(frameSize)
  const yi = new Float64Array(frameSize)
  let activeFrames = 0

  for (let f = 0; f < frameStarts.length; f++) {
    if (frameLoopEnergy[f] < activeEnergyFloor) continue
    activeFrames++
    const loopStart = frameStarts[f]
    const micStart = loopStart + bulkDelay
    for (let k = 0; k < frameSize; k++) {
      const w = win[k]
      xr[k] = mic[micStart + k] * w
      yr[k] = loop[loopStart + k] * w
      xi[k] = 0
      yi[k] = 0
    }
    fftInPlace(xr, xi)
    fftInPlace(yr, yi)
    for (let b = 0; b < bins; b++) {
      const xR = xr[b]
      const xI = xi[b]
      const yR = yr[b]
      const yI = yi[b]
      Sxx[b] += xR * xR + xI * xI
      Syy[b] += yR * yR + yI * yI
      // X · conj(Y)
      SxyRe[b] += xR * yR + xI * yI
      SxyIm[b] += xI * yR - xR * yI
    }
  }

  if (activeFrames < minActiveFrames) return { mode: "unknown", confidence: 0 }

  // Power-weighted mean coherence across the speech band (weight by loopback power
  // so silent bins don't dominate the average).
  let weighted = 0
  let weight = 0
  for (let b = loBin; b <= hiBin; b++) {
    const denom = Sxx[b] * Syy[b]
    if (denom < EPS) continue
    const coh = (SxyRe[b] * SxyRe[b] + SxyIm[b] * SxyIm[b]) / denom
    weighted += clamp01(coh) * Syy[b]
    weight += Syy[b]
  }
  const meanCoherence = weight > EPS ? weighted / weight : 0

  const diagnostics: IEchoCoherenceDiagnostics = {
    meanCoherence,
    delaySamples: bulkDelay,
    peakProminence,
    activeFrames,
  }

  if (meanCoherence >= coherenceThreshold) {
    const conf = clamp01((meanCoherence - coherenceThreshold) / (1 - coherenceThreshold))
    return { mode: "far-end-in-mic", confidence: clamp01(0.5 + conf / 2), diagnostics }
  }
  const conf = clamp01((coherenceThreshold - meanCoherence) / coherenceThreshold)
  return { mode: "far-end-absent", confidence: clamp01(0.5 + conf / 2), diagnostics }
}
